import { z } from 'zod';
/**
 * Keycloak Organization as surfaced through the Cortex API. The Organization
 * is the multi-tenancy boundary: every workflow, run, datasource, etc. is
 * scoped to exactly one of these. Domains attach optional auto-membership
 * rules ("anyone with @intel.com lands in this org").
 */
export const OrgViewSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    alias: z.string().nullable(),
    description: z.string().nullable(),
    enabled: z.boolean(),
    domains: z.array(z.object({ name: z.string(), verified: z.boolean() })).default([]),
});
/**
 * Single member of an organization. `name` is the display name (first + last),
 * already joined for UI consumption.
 */
export const OrgMemberSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string().nullable(),
    enabled: z.boolean(),
    role: z.enum(['customer', 'fde']).default('customer'),
});
export const OrgMemberRoleSchema = z.object({ role: z.enum(['customer', 'fde']) }).strict();
/**
 * Request body for inviting a new user. `redirectUri` is where Keycloak should
 * land the invitee after they finish the action-token flow (set password etc.);
 * the API falls back to the originating app's URL if omitted.
 */
export const InviteOrgMemberRequestSchema = z
    .object({
    email: z.string().email(),
    name: z.string().min(1).optional(),
    redirectUri: z.string().url().optional(),
    clientId: z.string().min(1).optional(),
})
    .strict();
/**
 * Superuser-only: create a new organization. Domains are optional (one or none);
 * `redirectUrl` is the default landing URL for invitees of this org.
 */
export const CreateOrgRequestSchema = z
    .object({
    name: z.string().min(1),
    alias: z.string().min(1),
    redirectUrl: z.string().url().optional(),
    domain: z.string().min(1).optional(),
})
    .strict();
/**
 * Aggregate counts surfaced on the superuser admin dashboard.
 */
export const RealmStatsSchema = z.object({
    organizationCount: z.number().int().nonnegative(),
    userCount: z.number().int().nonnegative(),
});
/**
 * Toggle the `enabled` flag on a user or organization (soft-delete /
 * restore). Used by PATCH endpoints — the API does not expose hard delete.
 */
export const SetEnabledRequestSchema = z.object({ enabled: z.boolean() }).strict();
//# sourceMappingURL=organization.js.map